import React, {CSSProperties} from 'react';
import {MediaPlayer} from '@focusrite-novation/ampify-web-ui';
import {BLOCKS, INLINES} from '@contentful/rich-text-types';
import {
  ContentfulRichTextGatsbyReference,
  renderRichText,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text';
import {Quote} from '../components/Quote/Quote';
import {BlogEmbeddedAsset} from '../components/BlogEmbeddedAsset/BlogEmbeddedAsset';
import Theme from '../theme';
import {BlogEmbeddedAssetCaption} from '../components/BlogEmbeddedAsset/BlogEmbddedAsset.styles';
import {Options} from '@contentful/rich-text-react-renderer';
import styled from 'styled-components';
import {FastCollectionCard} from '../components/FastCollection';

const headingStyle = {
  textAlign: 'center',
  margin: `${Theme.space[5]}px 0`,
} as CSSProperties;

const Text = ({children}: {children: React.ReactNode}) => (
  <p style={{margin: `${Theme.space[5]}px 0`}}>{children}</p>
);

// 15px is the default styling for list elements
const DefaultMarginOL = styled.ol`
  margin-left: 15px;
  li {
    padding-left: 15px;
  }
`;

export const getRenderOptions = (options?: RichTextRenderOptions): Options => {
  const isPreview = options?.isPreview ?? false;
  const isBlogPost = options?.isBlogPost ?? true;
  const withoutListStyles = options?.withoutListStyles ?? false;
  return {
    renderNode: {
      [BLOCKS.UL_LIST]: (node) => {
        return node.content.map((point: any, i: number) => {
          const textValue = point?.content?.[0]?.content?.[0]?.value;
          if (withoutListStyles && textValue) {
            return textValue && <p key={`textValue-${i}`}>{textValue}</p>;
          }

          if (textValue) {
            return <li key={`textValue-${i}`}>{textValue}</li>;
          }
        });
      },
      [BLOCKS.OL_LIST]: (node) => {
        return (
          <DefaultMarginOL>
            {node.content.map((point: any, i: number) => {
              const textValue = point?.content?.[0]?.content?.[0]?.value;
              if (textValue) {
                return <li key={`textValue-${i}`}>{textValue}</li>;
              }
            })}
          </DefaultMarginOL>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const type = isPreview
          ? node.data.target.sys.contentType.sys.id
          : node.data?.target?.__typename;

        if (type) {
          const embedUrls = [
            'ContentfulYoutubeEmbed',
            'ContentfulEmbeddableUrl',
            'embeddableUrl',
            'youtubeEmbed',
          ];

          const productEmbeds = [
            'ContentfulFastCollectionCard',
            'fastCollectionCard',
          ];

          if (productEmbeds.includes(type)) {
            const props = isPreview
              ? node?.data?.target.fields
              : node?.data?.target;

            return (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <FastCollectionCard product={props} maxWidthPercentage={45} />
              </div>
            );
          }

          const {quote, author, url, title, embeddableUrl} = isPreview
            ? node?.data?.target.fields
            : node?.data?.target;

          if (embedUrls.includes(type)) {
            return <MediaPlayer url={url ?? embeddableUrl} title={title} />;
          }

          return <Quote text={quote} author={author} />;
        }
      },
      [BLOCKS.PARAGRAPH]: (node, children: React.ReactNode) => {
        if (isBlogPost) {
          return <Text>{children}</Text>;
        }
        return <p>{children}</p>;
      },

      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const {title, fluid, description} = node.data.target;
        if (isPreview) {
          const {title, file, description} = node.data.target.fields;
          return (
            <div>
              <img src={file.url} alt={title} data-testid="image" />
              <BlogEmbeddedAssetCaption>
                {description.toUpperCase()}
              </BlogEmbeddedAssetCaption>
            </div>
          );
        }

        return (
          <BlogEmbeddedAsset
            fluid={fluid}
            alt={title}
            description={description}
          />
        );
      },
      [INLINES.HYPERLINK]: ({content, data}) => {
        return <a href={data.uri}>{(content?.[0] as any).value}</a>;
      },
      [BLOCKS.HEADING_1]: (node, children: React.ReactNode) => (
        <h1 style={headingStyle}>{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node, children: React.ReactNode) => (
        <h2 style={headingStyle}>{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node, children: React.ReactNode) => (
        <h3 style={headingStyle}>{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (node, children: React.ReactNode) => (
        <h4 style={headingStyle}>{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (node, children: React.ReactNode) => (
        <h5 style={headingStyle}>{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (node, children: React.ReactNode) => (
        <h6 style={headingStyle}>{children}</h6>
      ),
    },
  };
};

interface RichTextRenderOptions {
  isPreview?: boolean;
  isBlogPost?: boolean;
  withoutListStyles?: boolean;
}

export const renderPageContent = (
  richText: RenderRichTextData<ContentfulRichTextGatsbyReference>,
  options?: RichTextRenderOptions
) => renderRichText(richText, getRenderOptions(options));
